import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useIntl } from 'react-intl'
import { messages } from '../../messages'
import { RouteDestinationLoadAction, RouteDestinationLoadService, LoadMetadata, DestinationActionTypeId } from '../plannerTypes'

type Service = {
  id: number
  name: string
}

type LoadActionsTableProps = {
  loadActions: RouteDestinationLoadAction[]
  loadMetadataById: Record<number, LoadMetadata>
  loadServices?: RouteDestinationLoadService[]
  services?: Service[]
}

const getActionTypeName = (actionTypeId: number, formatMessage: (msg: any) => string): string => {
  switch (actionTypeId) {
    case DestinationActionTypeId.Pickup:
      return formatMessage(messages.pickup)
    case DestinationActionTypeId.DropOff:
      return formatMessage(messages.dropoff)
    default:
      return formatMessage(messages.notAvailable)
  }
}

const LoadActionsTable = ({
  loadActions,
  loadMetadataById,
  loadServices = [],
  services = [],
}: LoadActionsTableProps) => {
  const { formatMessage } = useIntl()

  const hasActions = loadActions.length > 0

  const getServiceNamesForLoad = (loadId: number): string => {
    const serviceIdsForLoad = loadServices
      .filter((ls) => ls.loadId === loadId)
      .map((ls) => ls.serviceId)
    
    if (serviceIdsForLoad.length === 0) {
      return '-'
    }

    const serviceNames = serviceIdsForLoad
      .map((serviceId) => {
        const service = services.find((s) => s.id === serviceId)
        return service?.name || `Service ${serviceId}`
      })
      .join(', ')

    return serviceNames || '-'
  }

  if (!hasActions) {
    return (
      <Box sx={{ p: 1, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {formatMessage(messages.noLoads)}
        </Typography>
      </Box>
    )
  }

  return (
    <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
            {formatMessage(messages.trackingNumber)}
          </TableCell>
          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
            {formatMessage(messages.action)}
          </TableCell>
          <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
            {formatMessage(messages.service)}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loadActions.map((action) => {
          const metadata = loadMetadataById[action.loadId]
          return (
            <TableRow key={action.id}>
              <TableCell sx={{ fontSize: '0.75rem' }}>
                {metadata?.trackingNumber || formatMessage(messages.notAvailable)}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem' }}>
                {getActionTypeName(action.destinationActionTypeId, formatMessage)}
              </TableCell>
              <TableCell sx={{ fontSize: '0.75rem' }}>
                {getServiceNamesForLoad(action.loadId)}
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

export default LoadActionsTable
